/**
 * Badges (storefront): the product's labels — New, Outlet, "до 4XL" — over
 * its pictures (the `badges` slot).
 *
 * The server sends the ones to show now (`product.badges`: [{ key, label,
 * color, computed? }], the computed "New" among them while `newUntil` is
 * ahead — aldoo-na badges). A product card shows them the same way:
 *
 *   import { ProductBadges } from "aldoo-ra/Store/extensions/badges/storefront"
 *   <ProductBadges product={product} size="sm" className="absolute top-2 left-2" />
 *
 * (the card's list has to ask for `extensions`, which brings the badges).
 */
import React from "react"
import { twMerge } from "tailwind-merge"

const DEFAULT_COLOR = "#111111"

/** A label in the reader's language: the text, or a { language: text } map's first. */
const labelOf = (label) => {
  if (typeof label === "string") return label
  if (!label || typeof label !== "object") return ""
  return label.en || Object.values(label).find((value) => typeof value === "string" && value) || ""
}

/** The badges a product shows: [{ key, label, color }] with a label. */
export const badgesOf = (product) =>
  (Array.isArray(product?.badges) ? product.badges : [])
    .map((badge) => ({ key: badge?.key || "", label: labelOf(badge?.label) || badge?.key || "", color: badge?.color || "" }))
    .filter((badge) => badge.label)

/** Black or white, whichever reads on the color (#rgb / #rrggbb; white otherwise). */
const inkOn = (color) => {
  const hex = String(color || "").replace("#", "")
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex
  if (!/^[0-9a-f]{6}$/i.test(full)) return "#ffffff"
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16))
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "#111111" : "#ffffff"
}

export function Badge({ badge, size = "md" }) {
  const background = badge.color || DEFAULT_COLOR
  return (
    <span
      className={twMerge(
        "inline-flex items-center rounded font-bold uppercase tracking-wide leading-none shadow-sm",
        size === "sm" ? "px-1.5 py-1 text-[10px]" : "px-2.5 py-1.5 text-[12px]"
      )}
      style={{ background, color: inkOn(background) }}
      data-testid={`badge-${badge.key || "label"}`}
    >
      {badge.label}
    </span>
  )
}

/** The product's badges in a row; nothing when it has none. */
export function ProductBadges({ product, size, className = "" }) {
  const badges = badgesOf(product)
  if (!badges.length) return null
  return (
    <div className={twMerge("flex flex-wrap gap-1.5", className)} data-testid="product-badges">
      {badges.map((badge, index) => (
        <Badge key={`${badge.key}-${index}`} badge={badge} size={size} />
      ))}
    </div>
  )
}

export const StorefrontExtension = {
  id: "badges",
  label: "Badges",
  slots: { badges: ProductBadges },
}
